import axios from "axios";
import { MAIN_URL } from "../../Configurations/Urls";

export function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function fetchRolePermissionCatalog() {
  const res = await axios.get(`${MAIN_URL}/api/core/role-permission-assignment`, {
    headers: authHeaders(),
  });
  return res.data || {};
}

export function flattenRolesFromCatalog(catalog) {
  const applications = catalog.applications || [];
  const rolesByApplication = catalog.roles_by_application || {};
  const rows = [];

  applications.forEach((app) => {
    const appId = app.application_id;
    const roles =
      rolesByApplication[appId] || rolesByApplication[String(appId)] || [];

    roles.forEach((role) => {
      rows.push({
        id: role.application_user_role_id,
        application_user_role_id: role.application_user_role_id,
        name: role.user_role_name,
        user_role_name: role.user_role_name,
        description: role.description || "",
        application_id: appId,
        application_name: app.application_name || `App ${appId}`,
      });
    });
  });

  return rows.sort((a, b) =>
    `${a.application_name} ${a.name}`.localeCompare(
      `${b.application_name} ${b.name}`
    )
  );
}

export function formatActionLabel(action) {
  const display = (
    action.module_feature_action_name ||
    action.module_action_name ||
    ""
  ).trim();
  if (display) return display;
  const code = (action.module_action_code || action.display_name || "").trim();
  if (code) return code;
  return `ACTION_${action.application_module_action_id}`;
}

function normalizedActionText(action) {
  return String(
    action.module_action_code ||
      action.module_action_name ||
      action.display_name ||
      ""
  )
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_");
}

function actionSortPriority(action) {
  const text = normalizedActionText(action);
  if (/(^|_)VIEW(_|$)/.test(text)) return 0;
  if (/(^|_)ADD(_|$)|(^|_)CREATE(_|$)/.test(text)) return 1;
  if (/(^|_)EDIT(_|$)|(^|_)UPDATE(_|$)/.test(text)) return 2;
  if (/(^|_)DELETE(_|$)|(^|_)REMOVE(_|$)/.test(text)) return 3;
  return 4;
}

function compareActionsForDisplay(a, b) {
  const pr = actionSortPriority(a) - actionSortPriority(b);
  if (pr !== 0) return pr;
  return formatActionLabel(a).localeCompare(formatActionLabel(b));
}

export function groupActions(actions) {
  const grouped = {};
  (actions || []).forEach((action) => {
    const label = action.feature_name || action.category || "Uncategorized";
    if (!grouped[label]) grouped[label] = [];
    grouped[label].push(action);
  });
  return Object.entries(grouped).map(([category_name, items]) => ({
    category_name,
    actions: [...items].sort(compareActionsForDisplay),
  }));
}

export function permissionsFromAllowedKeys(roleId, allowedKeys) {
  const list = [];
  (allowedKeys || []).forEach((key) => {
    const [rid, actionId] = String(key).split(":").map(Number);
    if (rid === Number(roleId)) {
      list.push({
        application_module_action_id: actionId,
        permission_allowed: true,
      });
    }
  });
  return list;
}

export async function fetchRolePermissions(roleId) {
  const res = await axios.get(
    `${MAIN_URL}/api/application/role-permissions/particular/${roleId}`,
    { headers: authHeaders() }
  );
  return res.data || {};
}

export async function saveRolePermissions(roleId, permissions) {
  await axios.put(
    `${MAIN_URL}/api/core/role-permission-assignment`,
    {
      application_user_role_id: Number(roleId),
      permissions,
    },
    {
      headers: {
        ...authHeaders(),
        "Content-Type": "application/json",
      },
    }
  );
}

export async function deleteRolePermissions(roleId) {
  await axios.delete(
    `${MAIN_URL}/api/application/role-permissions/particular/${roleId}`,
    { headers: authHeaders() }
  );
}

export function modulesForApplication(catalog, applicationId) {
  if (!applicationId) return [];
  const modulesByApplication = catalog.modules_by_application || {};
  return (
    modulesByApplication[applicationId] ||
    modulesByApplication[String(applicationId)] ||
    []
  );
}

export function rolesForApplication(catalog, applicationId) {
  if (!applicationId) return [];
  const rolesByApplication = catalog.roles_by_application || {};
  return (
    rolesByApplication[applicationId] ||
    rolesByApplication[String(applicationId)] ||
    []
  );
}
